// Nothing is asked first, because nothing is left to ask; the small "download or send" window opens
// AFTER the run, so it can never take an answer it turns out it cannot use. CUSTOM HAS NO CUSTOMIZE
// CHIP: clicking anywhere on it opens the flow.
import { useUiMode, showsAdvancedControls } from "../stores/uiModeStore";
import { useEffect, useState } from "react";
import { Settings2, X } from "lucide-react";
import { useSpreadRx } from "../hooks/useSpreadPreview";
import { useHydratedRecSets } from "../hooks/useHydratedRecSets";
import { useOrgStore } from "../stores/orgStore";
import { useWizard } from "../features/wizard/wizardStore";
import { formatMultiplier } from "../lib/limeMultiplier";
import { productLbsPerAc } from "../lib/spreadExport";
import { runSpreadPreset, type SpreadRunResult } from "../lib/spreadRun";
import { SPREAD_PRESETS, type SpreadPreset } from "../lib/spreadPresets";
import { BuildRxPreviewMap } from "./BuildRxPreviewMap";
import { SpreadDeliverModal } from "../features/spread-deliver/SpreadDeliverModal";
import { SpreadEditor } from "./SpreadEditor";
import type {
  DashboardField,
  ManifestLayer,
  SoilSamplePoint,
} from "../types/contract";

/* * Stable empty array — a fresh `` each render would remount the map's point source every time. */
const EMPTY_SAMPLES: SoilSamplePoint[] = [];

/* * A gridded rate surface for the card map — the same shape `buildValueLayer` gives the Rx layers. */
export function spreadLayer(
  geoms: { type?: string; coordinates?: unknown }[],
  rates: (number | null)[],
  title: string,
): ManifestLayer {
  const features = geoms.reduce<unknown[]>((acc, g, i) => {
    const v = rates[i];
    if (v != null && Number.isFinite(v))
      acc.push({ type: "Feature", properties: { value: v }, geometry: g });
    return acc;
  }, []);
  const present = rates.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  return {
    id: "spread-rate",
    kind: "geojson",
    title,
    // Explicit, because 'spread-rate' matches no id heuristic and any layer outside the known ramps
    // MUST carry its own direction.
    direction: "lower",
    inline: { type: "FeatureCollection", features } as never,
    colormap: "viridis",
    value_min: 0,
    value_max: present.length ? Math.max(...present) : 0,
    unit: "lbs/ac",
    default_visible: true,
    default_opacity: 1,
    order: 0,
    content_version: "spread",
    is_stale: false,
  } as ManifestLayer;
}

/* * One product card: the rate map for the first selected field, plus its description box. */
function SpreadCard({
  preset,
  field,
  hydrating,
  onPick,
  onCustomize,
}: {
  preset: SpreadPreset;
  /* * the server reconcile is still in flight — saying "no saved Rx" now would be a guess */
  hydrating: boolean;
  field: DashboardField;
  onPick: (p: SpreadPreset) => void;
  onCustomize: (p: SpreadPreset) => void;
}) {
  const uiMode = useUiMode((s) => s.mode);
  const wizardOn = useWizard((s) => s.wizard != null);
  const {
    data: rx,
    isLoading,
    error,
  } = useSpreadRx(field.field_id, preset.nutrient, !preset.custom);
  const rates =
    rx && preset.nutrient
      ? rx.rates.map((v) =>
          productLbsPerAc(v, preset.efficiencyPct, preset.nutrient!),
        )
      : null;
  const layer =
    rx && rates
      ? spreadLayer(
          rx.geoms,
          rates,
          preset.nutrient === "lime"
            ? `${preset.label} at ${formatMultiplier(preset.efficiencyPct)}× — lbs/ac`
            : `${preset.label} at ${preset.efficiencyPct}% — lbs/ac`,
        )
      : null;
  const present = rates?.filter((v): v is number => v != null) ?? [];
  const avg = present.length
    ? present.reduce((a, v) => a + v, 0) / present.length
    : null;
  const act = () => (preset.custom ? onCustomize(preset) : onPick(preset));

  return (
    <div className="flex gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-label={preset.label}
        onClick={act}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            act();
          }
        }}
        className="relative isolate flex h-[190px] min-w-0 flex-1 cursor-pointer flex-col overflow-hidden rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-accent"
        title={preset.custom ? "Choose nutrients" : `Spread: ${preset.label}`}
      >
        <div className="absolute left-2 top-2 z-scrim rounded bg-raised/90 px-2 py-0.5 text-[11px] font-semibold text-fg shadow">
          {preset.label}
        </div>

        {preset.custom ? (
          // Not a preset, so not a map: there is no default rate it could be showing.
          <div className="flex flex-1 items-center justify-center bg-hover/20 px-3 text-center text-[12px] font-medium text-muted">
            Click to Choose Nutrients
          </div>
        ) : layer ? (
          // The Build Rx card shows them because the question there is "what did the soil test
          // say"; here the card answers "what comes out of the spreader", and dots marking where
          // someone dug are noise on it.
          <BuildRxPreviewMap
            key={field.field_id}
            field={field}
            layer={layer}
            samples={EMPTY_SAMPLES}
            loading={false}
            error={false}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center bg-hover/20 px-3 text-center text-[11px] text-faint">
            {/* The saved-Rx cache is per-origin and the durable copy is on the server, so before the reconcile answers we do not KNOW the field has none — and announcing "no saved Rx" on a cache miss is exactly the bug hit on diyagronomy. Say what is actually true: we are still looking. */}
            {hydrating || isLoading
              ? "Reading saved Rx…"
              : error
                ? "That Rx can’t be read — open Customize for the reason."
                : `No saved ${preset.nutrient === "lime" ? "lime" : preset.nutrient === "p2o5" ? "phosphorus" : "potassium"} Rx on this field yet`}
          </div>
        )}

        {/* NOT on Custom: the whole card is that button there. */}
        {!preset.custom && (showsAdvancedControls(uiMode) || wizardOn) && (
          <button
            className="absolute bottom-2 right-2 z-scrim flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas shadow hover:bg-accent-hover"
            onClick={(e) => {
              e.stopPropagation();
              onCustomize(preset);
            }}
            data-wizard-anchor={`spread-customize-${preset.id}`}
            title="Pick a different Rx or change the analysis"
          >
            <Settings2 size={11} /> Customize
          </button>
        )}
      </div>

      <div className="flex h-[190px] w-44 shrink-0 flex-col overflow-auto rounded-lg border border-line bg-raised/50 p-2 text-[11px]">
        {/* the field average of the rate the card is SHOWING — the same number the file would carry */}
        {!preset.custom && (
          <div className="mb-1 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold text-accent">
            {isLoading
              ? "Field avg: …"
              : avg != null
                ? `Field avg: ${Math.round(avg)} lbs/ac`
                : "Field avg: —"}
          </div>
        )}
        {!preset.custom && rx && (
          <div
            className="mb-1 truncate rounded bg-hover/40 px-1.5 py-0.5 text-[10px] text-muted"
            title={rx.set.name}
          >
            Rx: {rx.set.name}
          </div>
        )}
        {!preset.custom && rx?.notice && (
          <div className="mb-1 text-[10px] text-muted" title={rx.notice}>
            {rx.notice}
          </div>
        )}
        {!preset.custom && rx?.fileNotice && (
          <div className="mb-1 text-[10px] text-muted" title={rx.fileNotice}>
            {rx.fileNotice}
          </div>
        )}
        {/*
 The efficiency reads as its own line as well as inside the sentence: it decides the rate
            and it is the thing Customize exists to change.
*/}
        {!preset.custom && (
          <div className="mb-1 rounded bg-hover/40 px-1.5 py-0.5 text-[10px] font-semibold text-muted">
            {preset.nutrient === "lime"
              ? `Multiplier: ${formatMultiplier(preset.efficiencyPct)}× (relative to 90% CCE lime, the recs default)`
              : `Analysis: ${preset.efficiencyPct}%`}
          </div>
        )}
        <div className="whitespace-pre-line text-muted">{preset.desc}</div>
      </div>
    </div>
  );
}

export function SpreadFertilizerPanel({
  fields,
  onClose,
}: {
  fields: DashboardField[];
  onClose: () => void;
}) {
  const field = fields[0];
  const multi = fields.length > 1;
  const orgName = useOrgStore((s) => s.activeOrg()?.name ?? "");

  const [editing, setEditing] = useState<SpreadPreset | null>(null);
  // WIZARD ACTION: the tour opens/closes the product editor. RE-REGISTERED on every `editing`
  // change: `editing` is component-local invisible to the host reconciler — re-registration bumps
  // actionsVersion, which IS a reconciler dep, so closing the editor (or opening the wrong
  // preset's) mid-step re-realizes the scene.
  useEffect(
    () =>
      useWizard.getState().registerAction("spread.edit", (arg) => {
        const id = (arg ?? null) as string | null;
        setEditing((cur) => {
          if ((cur?.id ?? null) === id) return cur;
          return id ? (SPREAD_PRESETS.find((p) => p.id === id) ?? null) : null;
        });
      }),
    [editing],
  );
  // Saved Rx are cached per ORIGIN in localStorage with the durable copy on the server; this panel
  // read the cache and never asked.
  const { hydrating } = useHydratedRecSets(fields.map((f) => f.field_id));
  const [ran, setRan] = useState<{
    preset: SpreadPreset;
    result: SpreadRunResult | null;
    busy: boolean;
    error: string | null;
  } | null>(null);

  // Clicking a card RUNS it: default analysis, most recent Rx, every selected field. Both decisions
  // are already made, so the only question left is where the file goes — which the modal asks after.
  const pick = (p: SpreadPreset) => {
    setRan({ preset: p, result: null, busy: true, error: null });
    void runSpreadPreset(p, fields, orgName)
      .then((result) =>
        setRan((r) =>
          r && r.preset.id === p.id ? { ...r, result, busy: false } : r,
        ),
      )
      .catch((e: unknown) =>
        setRan((r) =>
          r && r.preset.id === p.id
            ? {
                ...r,
                busy: false,
                error:
                  e instanceof Error
                    ? e.message
                    : "Couldn’t build the spread map.",
              }
            : r,
        ),
      );
  };
  const customize = (p: SpreadPreset) => setEditing(p);

  if (editing)
    return (
      <SpreadEditor
        preset={editing}
        fields={fields}
        onClose={() => setEditing(null)}
      />
    );

  return (
    <div className="mt-2 flex flex-col overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-md">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-3 py-1.5">
        <h2 className="text-[13px] font-semibold">
          Spread Fertilizer{" "}
          <span className="font-normal text-muted">— {field.field_name}</span>
        </h2>
        {multi && (
          <span className="text-[11px] text-faint">
            showing the first of {fields.length} fields — the product you pick
            spreads on all {fields.length}
          </span>
        )}
        <button
          onClick={onClose}
          className="ml-auto rounded p-1 hover:bg-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <div
        className="overflow-auto p-3"
        style={{ maxHeight: "min(460px, 60vh)" }}
      >
        <h1 className="mb-3 text-center text-lg font-bold text-fg sm:text-xl">
          What Are You Spreading?
        </h1>
        <div className="grid grid-cols-2 gap-3">
          {SPREAD_PRESETS.map((p) => (
            <SpreadCard
              key={p.id}
              preset={p}
              field={field}
              hydrating={hydrating}
              onPick={pick}
              onCustomize={customize}
            />
          ))}
        </div>
      </div>

      {ran && (
        <SpreadDeliverModal
          title={`Spread ${ran.preset.label}`}
          fields={fields}
          result={ran.result}
          growerName={orgName}
          ourProduct={ran.preset.label}
          busy={ran.busy}
          error={ran.error}
          onClose={() => setRan(null)}
        />
      )}
    </div>
  );
}
